import { getUrl } from '@/utils/getUrl';
import { isClient } from '@/utils/isClient';
import { isDev } from '@/utils/isDev';
import { isVideoPage } from '@/utils/isVideoPage';
import { logger } from '@/utils/logger';
import { tryGetWindowProp } from '@/utils/tryGetWindowProp';
import { waitFor } from '@/utils/waitFor';
import qs from 'query-string';
import { JW_CDN_URL } from '../constants';
import { ArticleInfo } from '../types/articleInfo';
import { replaceTag } from '../utils/replaceTag';

const addArticleDataToAdTag = ({
  tag,
  articleInfo,
  videoData,
  experimentGroup,
  adsPpId,
  isVerticalVideo,
  hasScrollArticles,
  posVideo,
  isPrebidAdtag,
  multivideoPrebid = false,
}: {
  tag: string;
  articleInfo?: ArticleInfo;
  videoData: any;
  experimentGroup: any;
  adsPpId: any;
  isVerticalVideo?: boolean;
  posVideo: string;
  isPrebidAdtag: boolean;
  hasScrollArticles?: boolean;
  explicitContent?: boolean;
  multivideoPrebid?: boolean;
  
}) => {
  const { articleVerticals = [], explicitContent = false } = articleInfo ?? {};
  const { wasYouplay } = videoData;
  const { url, query: tagQuery } = qs.parseUrl(tag);

  const custParams = tagQuery.cust_params || '';
  const [params] = Array.isArray(custParams) ? custParams : [custParams];

  const custParamsObj = qs.parse(params as string);
  // Add 'verticals'
  if (articleVerticals?.length) {
    // Put verticals in a set to create a list with unique values
    const verticalsSet = new Set([articleVerticals]);
    const verticals = Array.from(verticalsSet);

    custParamsObj['Vertikal'] = verticals.join(',');
  }

  if (experimentGroup) {
    custParamsObj['abgroup'] = experimentGroup;
  }
  // Add converted to youplay measurement
  if (wasYouplay) {
    // maybe have youplay ID here?

    custParamsObj['videoplayer'] = 'youplay';
  }
  multivideoPrebid && (custParamsObj['demandmanager'] = 'exp-pubx');
  isPrebidAdtag && (custParamsObj['demandmanager'] = '1');

  if (isVerticalVideo) {
    custParamsObj['page'] = 'floatingvideo';
  }
  if (hasScrollArticles) {
    custParamsObj['secondarticle'] = 'true';
  }
  if (explicitContent) {
    custParamsObj['explicit'] = 'true';
  }
  if (isPrebidAdtag) {
    custParamsObj['pos'] = posVideo;
  }

  return adsPpId
    ? `${url}?${qs.stringify({
        ...tagQuery,
        cust_params: qs.stringify(custParamsObj),
        ppid: adsPpId,
      })}`
    : `${url}?${qs.stringify({
        ...tagQuery,
        cust_params: qs.stringify(custParamsObj),
      })}`;
};

type WindowAds = {
  getAdScheduleId: () => any;
  getVideoExperimentGroup: () => any;
  getVideoPpId: () => string;
};

export const adsSetup = async ({
  articleInfo,
  isVerticalVideo,
  autoStart,
  wasYouplay,
  hasScrollArticles,
}: {
  articleInfo?: ArticleInfo;
  isVerticalVideo?: boolean;
  autoStart: string | boolean;
  wasYouplay: boolean;
  hasScrollArticles?: boolean;
}) => {
  let ads = null;
  ads = await tryGetWindowProp<WindowAds, undefined>('amav', { fallbackValue: undefined });

  if (!ads) {
    logger.warn(`adsSetup: window.ads not initialized`);
  }

  let adsScheduleId: any;
  let posVideo: string;
  const isVideo = isVideoPage();
  const multivideoPrebid = false;

  if (isDev) {
    adsScheduleId = process.env.OCELOT_ADS_SCHEDULE_ID;
  } else {
    try {
      const isViewable = autoStart === 'viewable';

      const startType = isViewable ? 'autoPlay' : 'clickToPlay';
      const pageType = isVideo ? 'videopage' : 'article';
      posVideo = isVideo ? 'videopage' : isViewable ? 'auto' : 'click';

      if (isClient() && ads) {
        const getAdScheduleId = window.amav?.getAdScheduleId;

        adsScheduleId = await getAdScheduleId?.(startType, pageType).catch((reason: string) => logger.debug(reason));
      }
    } catch (error: any) {
      logger.warn('Error thrown by getAdScheduleId', error);
    }
  }

  // Fetch is used instead of http to avoid issues with jwplayer CORS on mabra.com
  const response = await fetch(`${JW_CDN_URL}/advertising/schedules/${adsScheduleId}.json`);
  if (!response.ok) throw new Error(`Failed to fetch ad schedule: ${response.statusText}`);

  const data = await response.json();
  const schedule = data?.schedule;

  if (!schedule) {
    throw new Error(`No ad schedule found in supplied ad config`);
  }

  let experimentGroup: any = null;

  try {
    if (isClient() && ads) {
      experimentGroup = await window.amav?.getVideoExperimentGroup().catch((reason: string) => logger.debug(reason));
    }
  } catch (e: any) {
    logger.warn('Error thrown by window.ads.getVideoExperimentGroup', e);
  }

  const adsPpId = isClient() ? ads?.getVideoPpId() : null;

  let adTagUrl: string | undefined;
  adTagUrl = await tryGetWindowProp<string, undefined>('amaTagUrl', { fallbackValue: undefined });

  if (!adTagUrl) {
    logger.warn(`adsSetup: window.adTagUrl not initialized`);
  }

  const modifyTag = () => {
    let dfpIdVal: any;

    const adTag = adTagUrl
      ? `${adTagUrl.replace('securepubads.g', 'pubads.g').replace('output=vast', 'output=xml_vmap1')}`
      : '';
    const isPrebidAdtag = adTagUrl ? true : false;

    if (isClient()) {
      const url = getUrl(window.location.href);
      dfpIdVal = url?.searchParams.get('dfp_id');
    }

    if (dfpIdVal) {
      // It is from a test article, show a test ad.
      return schedule.map(({ tag }: { tag: string }) => ({
        tag: replaceTag(adTag || tag, dfpIdVal),
      }));
    } else {
      return schedule.map(({ tag }: { tag: string }) => ({
        tag: addArticleDataToAdTag({
          tag: adTag || tag,
          articleInfo,
          videoData: { wasYouplay },
          experimentGroup,
          adsPpId,
          isVerticalVideo,
          hasScrollArticles,
          posVideo,
          isPrebidAdtag,
          multivideoPrebid
        }),
      }));
    }
  };

  return {
    schedule: modifyTag(),
    adscheduleid: adsScheduleId,
    noConsent: false,
  };
};

export const adsTagSetup = async ({
  autoStart,
  hasScrollArticles: secondArticle,
  isVerticalVideo,
}: {
  isVerticalVideo?: boolean;
  autoStart: string | boolean;
  hasScrollArticles?: boolean;
}) => {
  logger.debug('adsTagSetup called with:', { autoStart });
  let ads = null;
  ads = await tryGetWindowProp<WindowAds, undefined>('amav', { fallbackValue: undefined });

  if (!ads) {
    logger.warn(`adsTagSetup: window.ads not initialized`);
  }

  let adTagUrl: any;

  try {
    const isViewable = autoStart === 'viewable';

    const startType = isViewable ? 'autoPlay' : 'clickToPlay';

    if (isClient() && ads) {
      await waitFor(() => typeof window.amav?.getAdTag === 'function', { timeout: 4000 });
      const getAdTag = window.amav?.getAdTag;

      adTagUrl = await getAdTag?.({ startType, isVerticalVideo, secondArticle }).catch((reason: string) =>
        logger.debug(reason),
      );
      logger.debug('adsTagSetup adTagUrl:', adTagUrl);
    }
  } catch (error: any) {
    logger.warn('Error thrown by getAdTag', error);
  }

  return {
    schedule: [
      {
        tag: adTagUrl,
        offset: 'pre',
        type: 'linear',
      },
    ],
    noConsent: false,
  };
};
type AdTagParams = Parameters<typeof adsTagSetup>[0];

const adTagParamsByPlayer = new WeakMap<object, AdTagParams>();

export const registerAdTagParams = (player: object, params: AdTagParams) => {
  adTagParamsByPlayer.set(player, params);
};

/**
 * Requests a fresh ad tag (new bids) for a video that replaces the one the player was set up with.
 * Returns a JW playlist item `adschedule`, or undefined when ads are not set up for the player.
 */
export const getFreshAdSchedule = async (player: object) => {
  const params = adTagParamsByPlayer.get(player);
  if (!params) return undefined;

  const { schedule } = await adsTagSetup(params);
  const [{ tag, offset, type }] = schedule;
  if (!tag) return undefined;

  return { nextVideoAd: { tag, offset, type } };
};

export const requestMultiVideoBids = async ({
  articleInfo,
  isVerticalVideo,
  autoStart,
  wasYouplay,
  hasScrollArticles,
}: {
  articleInfo?: ArticleInfo;
  isVerticalVideo?: boolean;
  autoStart: string | boolean;
  wasYouplay: boolean;
  hasScrollArticles?: boolean;
}) => {

  let ads = await tryGetWindowProp<WindowAds, undefined>('amav', { fallbackValue: undefined });
  if (!ads) {
    logger.warn(`adsSetup: window.ads not initialized`);
  }

  let adsScheduleId: string | undefined;
  let posVideo: string;
  const isVideo = isVideoPage();
  let multivideoPrebid = false;

  if (isDev) {
    adsScheduleId = process.env.OCELOT_ADS_SCHEDULE_ID;
  } else {
    try {
      const isViewable = autoStart === 'viewable';
      const startType = isViewable ? 'autoPlay' : 'clickToPlay';
      const pageType = isVideo ? 'videopage' : 'article';
      posVideo = isVideo ? 'videopage' : isViewable ? 'auto' : 'click';

      if (isClient() && ads) {
        const getAdScheduleId = window.amav?.getAdScheduleId;
        adsScheduleId = await getAdScheduleId?.(startType, pageType).catch((reason: string) =>
          logger.debug(reason)
        );
      }
    } catch (error) {
      logger.warn('Error thrown by getAdScheduleId', error);
    }
  }

 
  let adTagVMAPCacheUrl = await tryGetWindowProp<string, undefined>('amaVMAPCacheUrl', {
    fallbackValue: undefined,
  });
  if (adTagVMAPCacheUrl && typeof adTagVMAPCacheUrl === 'string') {
    logger.info(`adsSetup: Using VMAPCacheUrl`, adTagVMAPCacheUrl);
    adsScheduleId = 'LWXPTnkk';
  }
  
  if (!adsScheduleId) adsScheduleId = 'LWXPTnkk';

  const response = await fetch(`${JW_CDN_URL}/advertising/schedules/${adsScheduleId}.json`); 
  if (!response.ok) throw new Error(`Failed to fetch ad schedule: ${response.statusText}`);

  const data = await response.json();
  const schedule = data?.schedule;
  if (!schedule) throw new Error(`No ad schedule found in supplied ad config`);

  let experimentGroup: string | null = null;
  try {
    if (isClient() && ads) {
      experimentGroup = await window.amav
        ?.getVideoExperimentGroup()
        .catch((reason: string) => logger.debug(reason));
    }
  } catch (e) {
    logger.warn('Error thrown by window.ads.getVideoExperimentGroup', e);
  }

  const adsPpId = isClient() ? ads?.getVideoPpId() : null;
  

  let adTagUrl = await tryGetWindowProp<string | string[], undefined>('amaTagUrl', {
    fallbackValue: undefined,
  });
  if (!adTagUrl) {
    logger.warn(`adsSetup: window.adTagUrl not initialized`);
  }
  const modifyTag = () => {
    const applyArticleData = ({
      tag,
      isPrebidAdtag,
      multivideoPrebid,
    }: {
      tag: string;
      isPrebidAdtag: boolean;
      multivideoPrebid: boolean;
    }) =>
      addArticleDataToAdTag({
        tag,
        articleInfo,
        videoData: { wasYouplay },
        experimentGroup,
        adsPpId,
        isVerticalVideo,
        hasScrollArticles,
        posVideo,
        isPrebidAdtag,
        multivideoPrebid,
      });
  
    const getSchedule = (tag: string) => ({ tag, type: 'linear', offset: 'pre' });
  
    let dfpIdVal: string | null = null;
    if (isClient()) {
      const url = getUrl(window.location.href);
      dfpIdVal = url?.searchParams.get('dfp_id') ?? null;
    }
  
    const adTagUrls = adTagUrl ? (Array.isArray(adTagUrl) ? adTagUrl : [adTagUrl]) : [];
    const isPrebidAdtag = adTagUrls.length > 0;
  
    if (adTagVMAPCacheUrl && typeof adTagVMAPCacheUrl === 'string') {
      return adTagVMAPCacheUrl;
      
    }
  
    return schedule.flatMap(({ tag }: { tag: string }) =>
      adTagUrls.length > 0
        ? adTagUrls.map(singleAdTagUrl => {
            const normalizedTag = singleAdTagUrl
              .replace('securepubads.g', 'pubads.g')
              .replace('output=vast', 'output=xml_vmap1');
  
            const finalTag = dfpIdVal
              ? replaceTag(normalizedTag, dfpIdVal)
              : applyArticleData({
                  tag: normalizedTag || tag,
                  isPrebidAdtag,
                  multivideoPrebid: false,
                });
  
            return getSchedule(finalTag ?? '');
          })
        : [getSchedule(applyArticleData({ tag, isPrebidAdtag, multivideoPrebid: false }))]
    );
  };
  
  
  logger.debug(`adsSetup: Modified ad tags ready`, modifyTag());
  return {
    schedule: modifyTag(),
    adscheduleid: adsScheduleId,
    noConsent: false,
  };
};